<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useMetricsSnapshotStore } from '@/store/metricsSnapshotStore'
import StatusCard from './StatusCard.vue'
import LifecycleBreakdown from './LifecycleBreakdown.vue'
import AppMenuBarWithDateRange from '@/components/Menu/AppMenuBarWithDateRange.vue'
import { ArrowLeft, RefreshCw } from 'lucide-vue-next'
import type { MetricsSnapshot } from '@/models/MetricsSnapshot'
import { type LifecycleState, LIFECYCLE_STATE_OPTIONS, filterByLifecycleState } from '@/models/MetricsSnapshot'

const props = withDefaults(defineProps<{
  tenantId: string
  tenantName: string
  lifecycleState?: LifecycleState
}>(), {
  lifecycleState: 'all',
})

const emit = defineEmits<{
  'navigate-to-user': [payload: { tenantId: string; tenantName: string; userId: string; userName: string }]
  'navigate-back': []
  'update:lifecycleState': [state: LifecycleState]
}>()

const metricsStore = useMetricsSnapshotStore()

const searchText = ref('')
const selectedLifecycleState = ref<LifecycleState>(props.lifecycleState)

// Sync with prop changes from parent
watch(() => props.lifecycleState, (val) => {
  selectedLifecycleState.value = val
})

function handleLifecycleStateChange(value: LifecycleState) {
  selectedLifecycleState.value = value
  emit('update:lifecycleState', value)
}

// --- Data fetching ---

async function fetchData() {
  await metricsStore.fetchTenantMetrics(props.tenantId, {
    tenantOnly: false,
  })
}

function handleSearch(event: Event) {
  const target = event.target as HTMLInputElement
  searchText.value = target?.value ?? ''
}

// --- Computed ---

const loading = computed(() => metricsStore.loading.perTenant[props.tenantId] ?? false)

const snapshots = computed<MetricsSnapshot[]>(() => {
  const raw = metricsStore.perTenantData[props.tenantId]?.snapshots ?? []
  // Only use "all-time" snapshots (date range > 30 days) — filter out stale daily snapshots
  return raw.filter((s) => {
    if (!s.dateRangeStart || !s.dateRangeEnd) return true
    const start = new Date(s.dateRangeStart).getTime()
    const end = new Date(s.dateRangeEnd).getTime()
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24)
    return daysDiff > 30
  })
})

/** All snapshots (unfiltered) for lifecycle breakdown */
const allSnapshots = computed<MetricsSnapshot[]>(() => {
  return metricsStore.perTenantData[props.tenantId]?.snapshots ?? []
})

/** Tenant-level aggregate snapshot (ownerId === "__tenant__") */
const tenantAggregateSnapshots = computed(() => {
  const raw = snapshots.value.filter((s) => s.ownerId === '__tenant__')
  return filterByLifecycleState(raw, selectedLifecycleState.value)
})

/** Per-user snapshots (ownerId !== "__tenant__") */
const userSnapshots = computed(() => {
  const raw = snapshots.value.filter((s) => s.ownerId !== '__tenant__')
  return filterByLifecycleState(raw, selectedLifecycleState.value)
})

const aggregatedCounts = computed(() => {
  return metricsStore.aggregateCounts(tenantAggregateSnapshots.value)
})

const statusCards = computed(() => [
  { label: 'Total', count: aggregatedCounts.value.totalCount, variant: 'info' as const },
  { label: 'Succeeded', count: aggregatedCounts.value.succeededCount, variant: 'success' as const },
  { label: 'Failed', count: aggregatedCounts.value.failedCount, variant: 'danger' as const },
  { label: 'Running', count: aggregatedCounts.value.runningCount, variant: 'info' as const },
  { label: 'Pending', count: aggregatedCounts.value.pendingCount, variant: 'warning' as const },
  { label: 'Cancelled', count: aggregatedCounts.value.cancelledCount, variant: 'neutral' as const },
  { label: 'Throttled', count: aggregatedCounts.value.throttledCount, variant: 'warning' as const },
  { label: 'Rejected', count: aggregatedCounts.value.rejectedCount, variant: 'danger' as const },
])

/** Build per-user rows from user snapshots */
const userRows = computed(() => {
  const userMap = new Map<string, {
    userId: string
    userName: string
    email: string
    succeeded: number
    failed: number
    running: number
    total: number
  }>()

  for (const snap of userSnapshots.value) {
    const existing = userMap.get(snap.ownerId)
    if (existing) {
      existing.succeeded += snap.succeededCount
      existing.failed += snap.failedCount
      existing.running += snap.runningCount
      existing.total += snap.totalCount
    } else {
      userMap.set(snap.ownerId, {
        userId: snap.ownerId,
        userName: snap.ownerName || snap.ownerId,
        email: snap.ownerEmail || snap.ownerId,
        succeeded: snap.succeededCount,
        failed: snap.failedCount,
        running: snap.runningCount,
        total: snap.totalCount,
      })
    }
  }

  return Array.from(userMap.values())
})

/** Filter user rows by search text (case-insensitive substring match on name, email, or ID) */
const filteredUserRows = computed(() => {
  const query = searchText.value.toLowerCase().trim()
  if (!query) return userRows.value
  return userRows.value.filter(
    (row) =>
      row.userName.toLowerCase().includes(query) ||
      row.email.toLowerCase().includes(query) ||
      row.userId.toLowerCase().includes(query),
  )
})

// --- Table columns ---

const columns = [
  {
    title: 'User Name',
    dataIndex: 'userName',
    key: 'userName',
    sorter: (a: any, b: any) => a.userName.localeCompare(b.userName),
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    sorter: (a: any, b: any) => a.email.localeCompare(b.email),
  },
  {
    title: 'Succeeded',
    dataIndex: 'succeeded',
    key: 'succeeded',
    sorter: (a: any, b: any) => a.succeeded - b.succeeded,
  },
  {
    title: 'Failed',
    dataIndex: 'failed',
    key: 'failed',
    sorter: (a: any, b: any) => a.failed - b.failed,
  },
  {
    title: 'Running',
    dataIndex: 'running',
    key: 'running',
    sorter: (a: any, b: any) => a.running - b.running,
  },
  {
    title: 'Total',
    dataIndex: 'total',
    key: 'total',
    sorter: (a: any, b: any) => a.total - b.total,
  },
]

// --- Navigation ---

function handleRowClick(record: any) {
  emit('navigate-to-user', {
    tenantId: props.tenantId,
    tenantName: props.tenantName,
    userId: record.userId,
    userName: record.userName,
  })
}

function handleBack() {
  emit('navigate-back')
}

// --- Menu bar buttons ---

const navButtons = ref([
  {
    icon: ArrowLeft,
    onClick: handleBack,
    title: '',
    toolTip: 'Back to Summary',
  },
  {
    icon: RefreshCw,
    onClick: fetchData,
    animate: loading,
    title: '',
    toolTip: 'Refresh',
  },
])

// --- Lifecycle ---

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div>
    <!-- Menu bar with back button, date range, and search -->
    <AppMenuBarWithDateRange
      :showSearch="true"
      :showDateRange="false"
      :onSearch="handleSearch"
      :buttons="navButtons"
    />

    <!-- Lifecycle state filter -->
    <div class="mb-4">
      <a-segmented
        v-model:value="selectedLifecycleState"
        :options="LIFECYCLE_STATE_OPTIONS"
        @change="handleLifecycleStateChange"
      />
    </div>

    <!-- Tenant header -->
    <div class="mb-4">
      <h2 class="text-lg font-semibold text-foreground">{{ tenantName }}</h2>
      <p class="text-sm text-muted-foreground">{{ tenantId }}</p>
    </div>

    <!-- Status Cards -->
    <div class="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
      <StatusCard
        v-for="card in statusCards"
        :key="card.label"
        :label="card.label"
        :count="card.count"
        :variant="card.variant"
        :loading="loading"
      />
    </div>

    <!-- Lifecycle breakdown (visible only when filter is "All") -->
    <LifecycleBreakdown
      :snapshots="allSnapshots"
      :loading="loading"
      :visible="selectedLifecycleState === 'all'"
    />

    <!-- Empty state -->
    <div v-if="!loading && snapshots.length === 0" class="py-12 text-center text-muted-foreground">
      No metrics available for the selected period.
    </div>

    <!-- User table -->
    <div v-else class="overflow-x-auto">
      <!-- Skeleton loading for table -->
      <div v-if="loading && userSnapshots.length === 0" class="space-y-3">
        <Skeleton v-for="i in 5" :key="i" class="h-12 w-full" />
      </div>

      <a-table
        v-else
        :dataSource="filteredUserRows"
        :columns="columns"
        :pagination="{ showSizeChanger: filteredUserRows.length >= 20 }"
        :rowKey="(record: any) => record.userId"
        :customRow="(record: any) => ({
          onClick: () => handleRowClick(record),
          style: 'cursor: pointer',
        })"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'userName'">
            <a-tooltip>
              <template #title>{{ record.userId }}</template>
              {{ record.userName }}
            </a-tooltip>
          </template>

          <template v-else-if="column.key === 'email'">
            {{ record.email }}
          </template>

          <template v-else-if="column.key === 'succeeded'">
            <Badge class="bg-green-500 hover:bg-green-500 text-white">
              {{ record.succeeded.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'failed'">
            <Badge class="bg-red-500 hover:bg-red-500 text-white">
              {{ record.failed.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'running'">
            <Badge class="bg-blue-500 hover:bg-blue-500 text-white">
              {{ record.running.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'total'">
            {{ record.total.toLocaleString() }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>
